import { ErrorOutlineIcon } from '@ikyucom/design-system-icons-react'
import { CheckBox, ElementButton, InputText } from 'macaron-frontend-ui'
import { useCallback } from 'react'
import type { ComponentProps } from 'react'
import Image from '~/components/image/Image'
import { useEnv } from '~/env'
import { useErrorOnBlur } from '~/features/reservationInput/hooks/useErrorOnBlur'
import { useMemberRequiredCardPaymentErrorMessage } from '~/features/reservationInput/store/paymentMethod'
import { PAYPAY_IMAGE_URL } from '~/lib/constants/constants'
import { money } from '~/lib/money/money'
import { useIsPC } from '~/lib/ua'
import { getLoginUrl, getMemberRegistrationUrl } from '~/lib/url'
import { useRedirectBackUrlToReserveInput, useSendClickEvent } from '~/navigation'
import { css, sva } from '~/styled-system/css'
import { useGivenPoints } from '../../store/payment'
import { useLoginProps } from '../../store/reserve'
import { useIsPreMember } from '../../store/stateMachine/state'
import {
  useIsEmailRegistered,
  useIsPhoneNumberRegistered,
  usePasswordError,
  useSetPassword,
  useTransitionPreMemberChecked,
} from '../../store/userInput'
import { Section } from '../Section'
import { Email, EmailInput } from './Email'
import { NameKana, NameKanaInput } from './NameKana'
import { PhoneNumber, PhoneNumberInput } from './PhoneNumber'

const guestStyles = sva({
  slots: ['container', 'head', 'title', 'annotation', 'content', 'required'],
  base: {
    container: {
      _pc: {
        bg: 'white',
        p: '6',
        spaceY: '3',
      },
    },
    head: {
      _pc: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexDirection: 'row',
      },
    },
    title: {
      _pc: {
        fontSize: '2xl',
        fontWeight: 'bold',
      },
    },
    annotation: {
      _pc: {
        order: 1,
      },
    },
    content: {
      spaceY: '5',
    },
    required: {
      spaceY: '4',
    },
  },
  variants: {
    position: {
      top: {
        head: {
          _pc: {
            flexDirection: 'column',
            alignItems: 'start',
          },
        },
        annotation: {
          _pc: {
            order: -1,
          },
        },
      },
      right: {
        head: {
          _pc: {
            flexDirection: 'row',
          },
        },
        annotation: {
          _pc: {
            order: 1,
          },
        },
      },
    },
  },
  defaultVariants: {
    position: 'right',
  },
})

export function Guest() {
  const isPC = useIsPC()
  const Guest_ = isPC ? PcGuest : SdGuest
  return <Guest_ />
}

function PcGuest() {
  const { annotation, position } = useAnnotation()
  const classes = guestStyles({ position })
  // TODO: Section を PC でも使えるようにする
  return (
    <div className={classes.container}>
      <LoginPointText />
      <div className={classes.head}>
        <h2 className={classes.title}>来店者情報</h2>
        {annotation && <div className={classes.annotation}>{annotation}</div>}
      </div>
      <div className={classes.content}>
        <div className={classes.required}>
          <RequiredPreMemberAlert />
          <GuestNameKana />
          <GuestPhoneNumber />
          <GuestEmail />
        </div>
        <IkyuOnlyPreMemberChecked />
      </div>
    </div>
  )
}

function SdGuest() {
  const { annotation, position } = useAnnotation()
  const classes = guestStyles()
  return (
    <>
      <div
        className={css({
          bgColor: 'white',
          px: '3',
          pt: '4',
        })}
      >
        <LoginPointText />
      </div>
      <Section
        title="来店者情報"
        annotation={annotation}
        annotationPosition={position}
        headerAlignItems="top"
      >
        <div className={classes.content}>
          <div className={classes.required}>
            <RequiredPreMemberAlert />
            <GuestNameKana />
            <GuestPhoneNumber />
            <GuestEmail />
          </div>
          <IkyuOnlyPreMemberChecked />
        </div>
      </Section>
    </>
  )
}

const loginPointTextStyles = sva({
  slots: ['root', 'text', 'span', 'point', 'pointValue', 'note'],
  base: {
    root: {
      rounded: 'sm',
      bgColor: 'gray.100',
      p: '3',
    },
    text: {
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      lineHeight: '[26px]',
    },
    span: {
      display: 'flex',
      alignItems: 'center',
      columnGap: '1',
      ml: '2',
    },
    point: {
      fontSize: 'sm',
      color: 'gray.800',
    },
    pointValue: {
      fontSize: 'xl',
    },
    note: {
      mt: '1',
      fontSize: { base: 'xs', _pc: 'sm' },
      color: 'gray.700',
    },
  },
})

const LoginPointText = import.meta.env.VITE_MODE === 'ppg' ? PpgLoginPointText : IkyuLoginPointText

function IkyuLoginPointText() {
  const { givenPoints, registered } = usePreMemberChecked()
  const { root, text, span, point, pointValue, note } = loginPointTextStyles()
  if (registered) {
    return null
  }

  return (
    <div className={root}>
      <p className={text}>
        <IkyuLogin />で
        <span className={span}>
          <Image src={PAYPAY_IMAGE_URL} width={16} height={16} alt="PayPay" />
          <span className={point}>
            <span className={pointValue}>{givenPoints}</span>円分もらえる
          </span>
        </span>
      </p>
      <p className={note}>※PayPayポイントの獲得には、Yahoo! JAPAN IDとの連携が必要です。</p>
    </div>
  )
}

function PpgLoginPointText() {
  return null
}

const useAnnotation = import.meta.env.VITE_MODE === 'ppg' ? usePpgAnnotation : useIkyuAnnotation

type AnnotationProps = {
  annotation: ComponentProps<typeof Section>['annotation']
  position: ComponentProps<typeof Section>['annotationPosition']
}

function useIkyuAnnotation(): AnnotationProps {
  return { annotation: null, position: 'right' }
}

function usePpgAnnotation(): AnnotationProps {
  return { annotation: <PpgLogin />, position: 'top' }
}

function IkyuLogin() {
  const { onOpen } = useIkyuLogin()
  return (
    <button type="button" onClick={onOpen(false)} className={css({ color: 'link' })}>
      会員登録・ログイン
    </button>
  )
}

function useIkyuLogin() {
  const { onOpen } = useLoginProps()
  const sendClickEvent = useSendClickEvent()
  const onOpenLoginModal = useCallback(
    (signupOnly: boolean) => {
      return () => {
        sendClickEvent({ login: 'login', click_event: 'ReserveInput/login/textLink' })
        onOpen(signupOnly)()
      }
    },
    [onOpen, sendClickEvent]
  )
  return { onOpen: onOpenLoginModal }
}

function PpgLogin() {
  const { loginUrl, registrationUrl } = usePpgLogin()
  return (
    <p className={css({ marginBottom: '3' })}>
      Yahoo! JAPAN IDで
      <a href={loginUrl} className={css({ color: 'link' })}>
        ログイン
      </a>
      または
      <a href={registrationUrl} className={css({ color: 'link' })}>
        ID新規取得
      </a>
      をすると、PayPayポイントが貯まります。
    </p>
  )
}

function usePpgLogin() {
  const env = useEnv()
  const redirectBackUrl = useRedirectBackUrlToReserveInput()
  const loginUrl = getLoginUrl(env, redirectBackUrl)
  const registrationUrl = getMemberRegistrationUrl(env, redirectBackUrl)
  return { loginUrl, registrationUrl }
}

const requiredPreMemberAlertStyles = sva({
  slots: ['container', 'heading', 'content', 'text'],
  base: {
    container: {
      spaceY: '2',
      border: '[1px solid]',
      borderColor: 'error.border',
      p: '3',
    },
    heading: {
      display: 'flex',
      spaceX: '1',
      fontSize: 'sm',
    },
    content: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      spaceX: '2',
      rounded: 'default',
      bg: 'gray.100',
      p: '2',
    },
    text: {
      flex: '1',
      fontSize: 'sm',
    },
  },
})

function RequiredPreMemberAlert() {
  const { error, onOpen } = useRequiredPreMemberAlert()
  const classes = requiredPreMemberAlertStyles()
  if (error === '') {
    return null
  }
  return (
    <div className={classes.container}>
      <div className={classes.heading}>
        <ErrorOutlineIcon className={css({ marginY: '[2px]', color: 'error.text' })} />
        <p className={css({ fontWeight: 'bold' })}>
          このご予約はオンラインカード決済のみご利用いただけます。
        </p>
      </div>
      <div className={classes.content}>
        <p className={classes.text}>ご利用にはログインまたは会員登録を行ってください。</p>
        <ElementButton
          label="ログイン／会員登録"
          color="login"
          size="xSmall"
          onClick={onOpen(false)}
        />
      </div>
    </div>
  )
}

function useRequiredPreMemberAlert() {
  const error = useMemberRequiredCardPaymentErrorMessage()
  const { onOpen } = useLoginProps()
  return { error, onOpen }
}

function GuestNameKana() {
  return (
    <NameKana>
      <NameKanaInput />
    </NameKana>
  )
}

function GuestPhoneNumber() {
  return (
    <PhoneNumber>
      <PhoneNumberInput />
    </PhoneNumber>
  )
}

function GuestEmail() {
  return (
    <Email>
      <EmailInput />
    </Email>
  )
}

const IkyuOnlyPreMemberChecked =
  import.meta.env.VITE_MODE === 'ppg' ? EmptyPreMemberChecked : PreMemberChecked

const preMemberCheckedStyles = sva({
  slots: ['container', 'summary', 'point'],
  base: {
    container: {
      spaceY: '2',
    },
    summary: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    point: {
      fontSize: 'sm',
      fontWeight: 'bold',
      color: 'point.text',
    },
  },
})

function PreMemberChecked() {
  const { isPreMember, givenPoints, onChange, registered } = usePreMemberChecked()
  const classes = preMemberCheckedStyles()
  // ../store/specification.md 参照
  if (registered) {
    return null
  }
  return (
    <div className={classes.container}>
      <div className={classes.summary}>
        <CheckBox label="会員登録をする" defaultChecked={isPreMember} onChange={onChange} />
        <p className={classes.point}>
          今回の予約で<span className={css({ fontSize: 'lg' })}>{money(givenPoints)}</span>
          ポイント獲得
        </p>
      </div>
      <PasswordInput />
    </div>
  )
}

function EmptyPreMemberChecked() {
  return null
}

function usePreMemberChecked() {
  const isPreMember = useIsPreMember()
  const givenPoints = useGivenPoints()
  const isEmailRegistered = useIsEmailRegistered()
  const isPhoneNumberRegistered = useIsPhoneNumberRegistered()
  const registered = isEmailRegistered || isPhoneNumberRegistered
  const change = useTransitionPreMemberChecked()
  const sendClickEvent = useSendClickEvent()
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      sendClickEvent({ member: 'onClick', click_event: 'ReserveInput/member/onClick' })
      change(e.target.checked)
    },
    [change, sendClickEvent]
  )
  return { isPreMember, givenPoints, onChange, registered }
}

const passwordInputStyles = sva({
  slots: ['container', 'heading', 'title', 'required', 'input', 'note', 'consent'],
  base: {
    container: {
      spaceY: '2',
      bg: 'gray.100',
      p: '2',
      rounded: 'default',
      md: {
        spaceY: '3',
        p: '3',
      },
    },
    heading: {
      spaceY: '1',
      md: {
        display: 'flex',
        spaceY: '0',
        spaceX: '2',
      },
    },
    title: {
      fontSize: 'md',
      md: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: '0',
        width: '[176px]',
      },
    },
    required: {
      ml: '1',
      fontSize: 'sm',
      color: 'accent.pink',
    },
    input: {
      width: 'full',
    },
    note: {
      fontSize: 'xs',
      color: 'gray.700',
      my: '1',
    },
    consent: {
      fontSize: 'xs',
      color: 'gray.800',
    },
  },
})

function PasswordInput() {
  const { error, onChange, isPreMember, onBlur } = usePasswordInput()
  const classes = passwordInputStyles()
  if (!isPreMember) {
    return null
  }
  return (
    <div className={classes.container}>
      <div className={classes.heading}>
        <p className={classes.title}>
          パスワード設定
          <span className={classes.required}>*</span>
        </p>
        <div className={classes.input}>
          <InputText
            errorMessage={error}
            width={300}
            placeholder="password"
            withSpread={true}
            type="password"
            onChange={onChange}
            onBlur={onBlur}
          />
        </div>
      </div>
      <p className={classes.note}>
        ※8～20文字、半角英数字・記号を2種以上
        <br />
        ※パスワードを入力し予約すると、会員登録が完了します。
      </p>
      <p className={classes.consent}>
        会員登録に伴い、
        <span className={css({ color: 'link' })}>
          <a href="https://www.ikyu.com/guidance/premierservice/" target="_blank" rel="noreferrer">
            会員特典
          </a>
          ・
          <a href="https://www.ikyu.com/help/ex/kiyaku.htm" target="_blank" rel="noreferrer">
            一休.com 会員規約
          </a>
          ・
          <a href="https://www.ikyu.co.jp/policy/" target="_blank" rel="noreferrer">
            個人情報保護方針
          </a>
        </span>
        に同意したものとみなされます。
      </p>
    </div>
  )
}

function usePasswordInput() {
  const change = useSetPassword()
  const isPreMember = useIsPreMember()
  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      change(e.target.value)
    },
    [change]
  )
  const passwordError = usePasswordError()
  const { error, onBlur } = useErrorOnBlur(passwordError)
  return {
    error,
    onChange,
    isPreMember,
    onBlur,
  }
}
